/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { Box, Chip, Typography, styled } from "@mui/material";

export const AssociatedRestaurantCard = styled(Box)({
	border: '1px solid #d7dde8',
	borderRadius: '12px',
	backgroundColor: '#ffffff',
	padding: '18px',
	boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
	maxHeight: '462px',
	display: 'flex',
	flexDirection: 'column',
	gap: '14px',
	overflow: 'hidden',
});

export const AssociatedRestaurantHeader = styled(Box)({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: '12px',
});

export const AssociatedRestaurantHeading = styled(Typography)({
	fontSize: '12px',
	lineHeight: 1.2,
	fontWeight: 700,
	letterSpacing: '0.08em',
	textTransform: 'uppercase',
	color: '#64748b',
});

export const AssociatedRestaurantHeadingCount = styled(Box)({
	minWidth: '22px',
	height: '22px',
	padding: '0 6px',
	borderRadius: '999px',
	backgroundColor: '#edf2f8',
	color: '#566579',
	fontSize: '11px',
	lineHeight: '22px',
	fontWeight: 700,
	textAlign: 'center',
	boxSizing: 'border-box',
});

export const AssociatedRestaurantCardsList = styled(Box)({
	display: 'grid',
	gap: '12px',
	gridAutoRows: 'max-content',
	alignContent: 'start',
	overflowY: 'auto',
	paddingRight: '4px',
	minHeight: 0,
	flex: 1,
});

export const AssociatedRestaurantLoaderWrap = styled(Box)({
	display: 'flex',
	alignItems: 'stretch',
	justifyContent: 'stretch',
	flex: 1,
	minHeight: '390px',
	'& > *': {
		width: '100%',
		height: '100%',
		minHeight: '390px',
	},
});

export const AssociatedRestaurantEmptyState = styled(Typography)({
	fontSize: '14px',
	lineHeight: 1.4,
	color: '#64748b',
	textAlign: 'center',
	margin: 'auto 0',
});

export const AssociatedRestaurantCardBody = styled(Box)({
	border: '1px solid #d7dde8',
	borderRadius: '10px',
	backgroundColor: '#fafcfe',
	padding: '12px',
	display: 'grid',
	alignSelf: 'start',
	gap: '10px',
});

export const AssociatedRestaurantCardHeader = styled(Box)({
	display: 'flex',
	alignItems: 'flex-start',
	justifyContent: 'space-between',
	gap: '10px',
});

export const AssociatedRestaurantName = styled(Typography)({
	fontSize: '18px',
	lineHeight: 1.2,
	fontWeight: 700,
	color: '#233044',
	wordBreak: 'break-word',
});

export const AssociatedRestaurantStatusChip = styled(Chip, {
	shouldForwardProp: (prop) => prop !== 'isActive',
})(({ isActive }) => ({
	height: '22px',
	borderRadius: '999px',
	backgroundColor: isActive ? '#dff8eb' : '#eceff3',
	color: isActive ? '#11a95f' : '#64748b',
	'& .MuiChip-label': {
		paddingLeft: '8px',
		paddingRight: '8px',
		fontSize: '10px',
		fontWeight: 700,
		lineHeight: 1,
	},
}));

export const AssociatedRestaurantTypeList = styled(Box)({
	display: 'flex',
	flexWrap: 'wrap',
	gap: '6px',
});

export const AssociatedRestaurantTypeChip = styled(Chip, {
	shouldForwardProp: (prop) => !['backgroundColor', 'textColor'].includes(prop),
})(({ backgroundColor, textColor }) => ({
	height: '24px',
	borderRadius: '7px',
	backgroundColor,
	color: textColor,
	'& .MuiChip-label': {
		paddingLeft: '8px',
		paddingRight: '8px',
		fontSize: '11px',
		fontWeight: 600,
		lineHeight: 1,
	},
}));

export const AssociatedRestaurantMetaRow = styled(Box)({
	display: 'flex',
	alignItems: 'center',
	gap: '8px',
	minWidth: 0,
});

export const AssociatedRestaurantMetaIcon = styled(Box)({
	width: '14px',
	height: '14px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	color: '#728398',
	flexShrink: 0,
});

export const AssociatedRestaurantMetaText = styled(Typography)({
	fontSize: '12px',
	lineHeight: 1.35,
	color: '#5d7188',
	fontWeight: 500,
});

export const AssociatedRestaurantRoleRow = styled(Box)({
	display: 'flex',
	alignItems: 'center',
	gap: '6px',
	minWidth: 0,
	flexWrap: 'wrap',
});

export const AssociatedRestaurantRoleItem = styled(Box)({
	display: 'flex',
	alignItems: 'center',
	gap: '6px',
	minWidth: 0,
});

export const AssociatedRestaurantRoleStatusDot = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'isArchived',
})(({ theme, isArchived }) => ({
	width: '8px',
	height: '8px',
	borderRadius: '999px',
	backgroundColor: isArchived ? theme.palette.error.main : theme.palette.success.main,
	flexShrink: 0,
}));

export const AssociatedRestaurantOverflowTrigger = styled(Box)({
	fontSize: '11px',
	lineHeight: 1,
	fontWeight: 700,
	color: '#556476',
	backgroundColor: '#eef2f6',
	borderRadius: '999px',
	padding: '4px 7px',
	cursor: 'default',
});

export const AssociatedRestaurantTooltipContent = styled(Box)({
	display: 'grid',
	gap: '4px',
	fontSize: '12px',
	lineHeight: 1.35,
	padding: '2px 0',
});

export const AssociatedRestaurantDiscountList = styled(Box)({
	display: 'flex',
	flexWrap: 'wrap',
	gap: '6px',
	marginTop: '2px',
});

export const AssociatedRestaurantDiscountChip = styled(Chip, {
	shouldForwardProp: (prop) => !['chipbackground', 'chiptextcolor'].includes(prop),
})(({ chipbackground, chiptextcolor }) => ({
	height: '22px',
	borderRadius: '999px',
	backgroundColor: chipbackground,
	color: chiptextcolor,
	'& .MuiChip-label': {
		paddingLeft: '9px',
		paddingRight: '9px',
		fontSize: '10px',
		fontWeight: 700,
		lineHeight: 1,
	},
}));
